import { useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import type { ThreadSender } from "./send";
import { acceptsRepositories, repositoryRequests, type RepositoryChoices, type RepositoryContext, type RunRepository } from "./repositories";

export function RepositoryPicker({ sender, selected, frozen, disabled, onChange, onReviewing }: {
  sender: ThreadSender; selected: RunRepository[]; frozen: boolean; disabled: boolean;
  onChange: (repositories: RunRepository[], context: RepositoryContext) => Promise<boolean>;
  onReviewing: (reviewing: boolean) => void;
}) {
  const [choices, setChoices] = useState<RepositoryChoices>();
  const [draft, setDraft] = useState<RunRepository[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>();
  const lifetime = useRef<AbortController | undefined>(undefined);
  useEffect(() => {
    function clear() {
      lifetime.current?.abort(); setChoices(undefined); setDraft([]); setLoading(false); setError(undefined); onReviewing(false);
    }
    function hidden() { if (document.visibilityState !== "visible") clear(); }
    clear();
    document.addEventListener("visibilitychange", hidden);
    return () => { lifetime.current?.abort(); onReviewing(false); document.removeEventListener("visibilitychange", hidden); };
  }, [sender, onReviewing]);

  async function open() {
    if (disabled || frozen || loading) return;
    lifetime.current?.abort();
    const controller = new AbortController(); lifetime.current = controller;
    setChoices(undefined); setDraft([]); setLoading(true); setError(undefined); onReviewing(true);
    try {
      const result = await sender.repositoryChoices(controller.signal);
      if (controller.signal.aborted) return;
      const initial = result.context.source === "snapshot" ? repositoryRequests(result.context.repositories)
        : result.context.source === "imported" && !acceptsRepositories(result.context, selected)
          ? result.context.repositories.map((row) => ({ ...row, mountAlias: "source", mode: "read" as const })) : selected;
      setDraft(initial); setChoices(result);
    } catch { if (!controller.signal.aborted) { setError("Code access couldn't be checked. Reload before sending."); onReviewing(false); } }
    finally { if (!controller.signal.aborted) setLoading(false); }
  }

  function close() {
    lifetime.current?.abort(); setChoices(undefined); setDraft([]); setError(undefined); setLoading(false); onReviewing(false);
  }
  const required = Boolean(choices?.context.repositories.length);
  const fixed = choices?.context.source === "snapshot";
  const resources = choices?.repositories.filter((row) => !required || choices.context.repositories.some((repo) => repo.repositoryId === row.id));
  const unavailable = choices && draft.some((row) => !choices.repositories.some((resource) => resource.id === row.repositoryId));
  let valid = false;
  try { valid = Boolean(choices && !unavailable && acceptsRepositories(choices.context, draft)); } catch { /* An unfinished branch or selection cannot be saved. */ }

  async function save() {
    if (!choices || disabled || loading || !valid) return;
    const controller = lifetime.current;
    setLoading(true); setError(undefined);
    const saved = await onChange(draft, choices.context).catch(() => false);
    if (controller?.signal.aborted) return;
    if (saved) close();
    else { setError("The selection wasn't saved. Reload to check current access."); setLoading(false); }
  }

  return <section aria-label="Code access" className="mt-3 space-y-3 text-sm text-[#52606e]">
    {selected.length ? <p>{selected.length} {selected.length === 1 ? "repository" : "repositories"} selected · {selected.some((row) => row.mode === "write") ? "One allows edits" : "Read only"}</p> : null}
    {!frozen ? <>
      {!choices ? <Button variant="ghost" disabled={disabled || loading} onClick={() => void open()}>{loading ? "Checking code access…" : "Choose code access"}</Button> : <div className="space-y-3 rounded-xl border border-[#cfe4f1] bg-white p-4">
        <p>{fixed ? "Continue with the same code and access as the saved work." : "Choose the code this message can use. Only one repository can allow edits."}</p>
        {resources?.map((resource, index) => {
          const value = draft.find((row) => row.repositoryId === resource.id);
          return <fieldset key={resource.id} disabled={disabled || loading} className="min-w-0 space-y-2 rounded-lg border border-[#cfe4f1] p-3">
            <legend className="max-w-full break-all px-1 font-medium text-[#20272b]">{resource.locator}</legend>
            <label className="block">Access for {resource.locator}
              <select value={value?.mode ?? "none"} disabled={fixed} onChange={(event) => {
                const mode = event.target.value;
                const rest = draft.filter((row) => row.repositoryId !== resource.id);
                if (mode === "none" && !required) setDraft(rest);
                else if ((mode === "read" || mode === "write") && (mode !== "write" || !rest.some((row) => row.mode === "write"))) setDraft([...rest, { repositoryId: resource.id, mountAlias: value?.mountAlias ?? `source-${index + 1}`, mode, revision: value?.revision ?? resource.defaultBranch ?? "" }]);
              }} className="mt-1 block w-full rounded-lg border border-[#cfe4f1] bg-white p-2 text-[#20272b] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#7376a8] disabled:opacity-60">
                {!required ? <option value="none">Not included</option> : null}
                <option value="read">Read</option>
                <option value="write" disabled={draft.some((row) => row.repositoryId !== resource.id && row.mode === "write")}>Edit</option>
              </select>
            </label>
            {value ? <label className="block">Branch or revision for {resource.locator}
              <input value={value.revision} disabled={required} autoCapitalize="none" autoCorrect="off" spellCheck={false} onChange={(event) => setDraft(draft.map((row) => row.repositoryId === resource.id ? { ...row, revision: event.target.value } : row))} className="mt-1 block w-full min-w-0 rounded-lg border border-[#cfe4f1] p-2 font-mono text-xs text-[#20272b] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#7376a8] disabled:opacity-60" />
            </label> : null}
          </fieldset>;
        })}
        {!resources?.length ? <p>No repositories are connected. A workspace admin can connect one.</p> : null}
        {unavailable && !required ? <><p>Some selected code is no longer available.</p><Button variant="ghost" disabled={disabled || loading} onClick={() => setDraft([])}>Clear code selection</Button></> : null}
        <div className="flex flex-wrap gap-2"><Button disabled={disabled || loading || !valid} onClick={() => void save()}>Use selection</Button><Button variant="ghost" disabled={disabled || loading} onClick={close}>Cancel code selection</Button><Button variant="ghost" disabled={disabled || loading} onClick={() => void open()}>Reload code access</Button></div>
      </div>}
      {error ? <p role="alert">{error}</p> : null}
    </> : null}
  </section>;
}
